/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Sebastian Hartmann <s.hartmann@techdivision.com>
 * @author Dominik Boeck <d.boeck@techdivision.com>
 */

import React, { useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import { Marker } from '../../../../StudioLocator/Components/Marker/Marker';
import { SelectedStudio } from '../../../../StudioLocator/Components/Overlay/SelectedStudio/SelectedStudio';
import './styles.scss';
import { useStudioLocator } from '../../../../StudioLocator/useStudioLocator';
import { studioMapConfig } from './config';

/**
 * StudioMap Component
 * @returns {Element}
 * @constructor
 */
export default function StudioMap(props) {
  const { onStudioSelect, studioId } = props;
  const mapConfiguration = studioMapConfig;

  const {
    isLoading,
    getMappedStudioData,
    currentSelectedMarker,
    setCurrentSelectedMarker,
    setSelectedMarker,
    mobileDetailsVisible,
  } = useStudioLocator(mapConfiguration.countryCode, studioId);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);

  /**
     * Handle the initialization of the maps api
     * @param map
     * @param maps
     */
  const handleApiLoaded = (map, maps) => {
    mapRef.current = map;
    mapsRef.current = maps;

    // After map is loaded zoom in closer
    const timer = setTimeout(() => {
      mapRef.current.setZoom(mapConfiguration.zoom);
    }, 1000);
    return () => clearTimeout(timer);
  };

  const selectCurrentStudio = (studio) => {
    setSelectedMarker(studio);
    if (onStudioSelect) {
      onStudioSelect(studio);
    }
  };

  /**
     * Render react wrapper for Google Maps
     * @returns {JSX.Element}
     */
  const renderStudioMap = () => {
    // While loading return the loading overlay and block the space for the map with a skeleton component
    if (isLoading) {
      return (
        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          height={mapConfiguration.defaultMapHeight}
        />
      );
    }

    return (
      <Box
        className="map-container rounded-map"
        sx={{ width: '100%', height: mapConfiguration.defaultMapHeight }}
      >
        <Box className="map" sx={{ width: '100%', height: '100%' }}>
          <GoogleMapReact
            bootstrapURLKeys={{
              key: mapConfiguration.apiKey,
              libraries: ['geometry', 'places'],
            }}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
            defaultCenter={mapConfiguration.center}
            defaultZoom={mapConfiguration.initialZoom}
          >
            {getMappedStudioData().map((studio) => (
              <Marker
                key={studio.id}
                onClick={() => selectCurrentStudio(studio)}
                alt={studio.name}
                lat={studio.address.latitude}
                lng={studio.address.longitude}
              />
            ))}
          </GoogleMapReact>
        </Box>
        <Box className={currentSelectedMarker ? 'overlay visible' : 'overlay'}>
          {currentSelectedMarker ? (
            <SelectedStudio
              studio={currentSelectedMarker}
              setCurrentSelectedMarker={setCurrentSelectedMarker}
              className={mobileDetailsVisible ? 'visible' : ''}
              showToStudioLink={false}
            />
          ) : null}
        </Box>
      </Box>
    );
  };

  return <>{renderStudioMap()}</>;
}

StudioMap.propTypes = {
  onStudioSelect: PropTypes.func,
  studioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

StudioMap.defaultProps = {
  onStudioSelect: null,
  studioId: null,
};
